import React, {useRef} from 'react';
import PropTypes from 'prop-types';
import {Form, Field} from 'react-final-form';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  Box,
  FileUpload,
  Grid,
  LoadingButton,
  Link,
  Text,
  useMediaQuery,
} from '@tenancy.nz/design-system';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';

import AddressFieldset from './AddressFieldset';
import Conditional from './Conditional';
import FieldCheckbox from './FieldCheckbox';
import PersonalDetailFieldset from './PersonalDetailFieldset';
import Section from './Section';
import SelectionGroup from './SelectionGroup';

import {env, getFieldProps, isRequired} from '../utils/helpers';

function RequestDataForm({initialValues, onIdUpload, onSubmit, ...rest}) {
  const reCaptchaRef = useRef();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const handleOnSubmit = async values => {
    if (reCaptchaRef.current.getValue()) {
      reCaptchaRef.current.reset();
    }
    const recaptcha = await reCaptchaRef.current.executeAsync();
    return onSubmit({...values, recaptcha});
  };

  const reasons = [
    {
      label: 'You showed up on my credit report',
      value: 'You showed up on my credit report',
    },
    {
      label: 'I applied for a property through your application form',
      value: 'I applied for a property through your application form',
    },
    {
      label: "I'm guessing",
      value: "I'm guessing",
    },
    {
      label: 'A Property Manager has informed me they use your system',
      value: 'A Property Manager has informed me they use your system',
    },
    {
      label: "I'm asking everyone I can, what data is on me",
      value: "I'm asking everyone I can, what data is on me",
    },
  ];

  return (
    <Form initialValues={initialValues} onSubmit={handleOnSubmit} {...rest}>
      {({handleSubmit, submitting, pristine, values, form}) => (
        <form
          onSubmit={event => {
            handleSubmit(event).then(() => {
              form.restart();
            });
          }}>
          <Section
            heading="Disclaimer"
            color="secondary"
            intro={
              <Text as="span" color="secondary" display="inline">
                This is really important!
              </Text>
            }
            introLabel={<InfoOutlinedIcon />}>
            <p>
              <strong>
                We do not hold data relating to why people are denied tenancies.
                If you require data that is not listed in the options below,
                please call us on{' '}
                <Link href="tel:0800 483 626">0800 483 626</Link> and speak to
                our Privacy Officer.
              </strong>
            </p>
            <p>
              <strong>
                Please contact the Property Manager for copies of your Tenancy
                Applications, Tenancy Agreements and your credit reports.
              </strong>
            </p>
          </Section>
          <Section heading="Why do you believe we have the data on you?">
            <Field name="reason" type="radio" validate={isRequired}>
              {({input, meta}) => (
                <SelectionGroup
                  type="radio"
                  options={reasons}
                  label=""
                  required
                  vertical
                  {...getFieldProps({
                    input,
                    meta,
                  })}
                />
              )}
            </Field>
          </Section>
          <Section heading="Please supply me with the following data">
            <FieldCheckbox
              label="List of who has run checks on me"
              name="supply_data"
              validate={isRequired}
            />
          </Section>
          <Section
            heading="Your Credit Reports"
            intro={
              <span>
                Please contact{' '}
                <Link
                  href="https://www.centrix.co.nz"
                  rel="noopener noreferrer"
                  target="_blank">
                  centrix.co.nz
                </Link>{' '}
                and{' '}
                <Link
                  href="https://www.equifax.co.nz"
                  rel="noopener noreferrer"
                  target="_blank">
                  equifax.co.nz
                </Link>{' '}
                for a free copy of your up to date Credit File
              </span>
            }>
            <FieldCheckbox
              label="I understand that I will not be sent a copy of my Credit Report from tenancy.co.nz"
              name="credit_report"
              validate={isRequired}
            />
          </Section>
          <Section
            heading="Tenancy Tribunal Orders"
            intro="We do not hold past Tenancy Tribunal orders">
            <FieldCheckbox
              label="I understand I will not be supplied with Tenancy Tribunal orders as part of this request"
              name="tribunal_order"
              validate={isRequired}
            />
          </Section>
          <Conditional
            show={
              !!values.reason &&
              values.supply_data &&
              values.credit_report &&
              values.tribunal_order
            }>
            <Section heading="Your Personal Details">
              <Box mt={4} mb={2}>
                <PersonalDetailFieldset />
              </Box>
            </Section>
            <Section heading="Your Address Details">
              <Box mt={4} mb={2}>
                <AddressFieldset />
              </Box>
            </Section>
            <Section
              heading="Identification"
              intro="To confirm we are sending data to the right person, please upload a copy of your ID. e.g passport, drivers license, 18+ card etc. requests submitted without ID will not be processed.">
              <Box mt={4} mb={2}>
                <Field name="id_proof">
                  {({input, meta}) => (
                    <FileUpload
                      {...getFieldProps({input, meta})}
                      files={values.id_proof ?? []}
                      label=""
                      dropzoneLabel=""
                      dropzoneDescription="Drag and drop file here, or click to select file"
                      helperText="Max 1 file (5mb), .jpeg, .pdf, .png file types are allowed."
                      helperTextPlacement="below"
                      dropzoneProps={{
                        disabled: (values.id_proof ?? []).length >= 1,
                        accept: ['image/*', 'application/pdf'],
                        // maxSize: maxUploadSize,
                      }}
                      onDelete={vals => {
                        input.onChange(vals);
                      }}
                      onChange={files => onIdUpload(files, input.onChange)}
                    />
                  )}
                </Field>
              </Box>
            </Section>
            <Section heading="Consent">
              <FieldCheckbox
                label="I confirm the details above are true and correct and I allow Tenant.co.nz to process my data request."
                name="accept"
                validate={isRequired}
              />
            </Section>
            <Grid container spacing={5}>
              <Grid item xs={12}>
                <ReCAPTCHA
                  sitekey={env('REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY')}
                  ref={reCaptchaRef}
                  size="invisible"
                />
                <LoadingButton
                  type="submit"
                  fill={isMobile}
                  color="primary"
                  loading
                  pending={submitting}
                  disabled={pristine}>
                  Submit
                </LoadingButton>
              </Grid>
            </Grid>
          </Conditional>
        </form>
      )}
    </Form>
  );
}

RequestDataForm.propTypes = {
  /**
   *
   */
  initialValues: PropTypes.shape({}),

  /**
   *
   */
  onIdUpload: PropTypes.func.isRequired,

  /**
   *
   */
  onSubmit: PropTypes.func.isRequired,
};

RequestDataForm.defaultProps = {
  initialValues: {},
};

export default RequestDataForm;
